import { memo } from "react";
import PropTypes from "prop-types";

import { Box, Tab, Tabs, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";

const getCountToneStyles = (theme, tabValue) => {
  const toneMap = {
    allOrders: {
      bg: theme.palette.info.lightest,
      color: theme.palette.primary.main,
    },
    dueDatePending: {
      bg: theme.palette.orange.light,
      color: theme.palette.orange.dark,
    },
    paymentPending: {
      bg: theme.palette.error.errorLightBgColor,
      color: theme.palette.error.errorTextColor,
    },
    doPending: {
      bg: theme.palette.info.lightBlueBackround,
      color: theme.palette.info.blueBorderColor,
    },
    doCreated: {
      bg: theme.palette.green.light,
      color: theme.palette.green.dark,
    },
    exceptions: {
      bg: theme.palette.grey[100],
      color: theme.palette.text.secondary,
    },
  };

  return toneMap[tabValue] || toneMap.exceptions;
};

const FGTrackingTabs = ({ tabs, selectedTab, onChange }) => {
  const theme = useTheme();

  return (
    <Tabs
      value={selectedTab}
      onChange={onChange}
      variant="scrollable"
      allowScrollButtonsMobile
      sx={{
        minHeight: 42,
        border: "1px solid",
        borderColor: theme.palette.grey[300],
        borderRadius: "12px",
        overflow: "hidden",
        backgroundColor: theme.palette.common.white,
        "& .MuiTabs-indicator": { display: "none !important" },
        ">.MuiTabs-scroller": {
          ">.MuiTabs-flexContainer": {
            minHeight: 42,
          },
        },
      }}
    >
      {tabs.map((tab, index) => {
        const isSelected = selectedTab === tab.value;
        const countTone = getCountToneStyles(theme, tab.value);

        return (
          <Tab
            key={tab.value}
            value={tab.value}
            disableRipple
            sx={{
              minHeight: 42,
              textTransform: "none",
              minWidth: "auto",
              padding: "0 16px",
              borderRadius: 0,
              borderRight: index === tabs.length - 1 ? "none" : "1px solid",
              borderColor: theme.palette.grey[300],
              color: isSelected
                ? theme.palette.common.white
                : theme.palette.primary.main,
              fontWeight: isSelected ? 700 : 500,
              backgroundColor: isSelected
                ? theme.palette.primary.main
                : theme.palette.common.white,
              transition: "background-color 0.2s ease, color 0.2s ease",
              "&.Mui-selected": {
                color: `${theme.palette.common.white} !important`,
                backgroundColor: theme.palette.primary.main,
              },
              "&:hover": {
                backgroundColor: isSelected
                  ? theme.palette.primary.main
                  : theme.palette.grey[100],
              },
            }}
            label={
              <Box
                display="flex"
                alignItems="center"
                columnGap={1}
                color="inherit"
              >
                <Typography
                  variant="body2"
                  sx={{
                    lineHeight: 1.2,
                    color: "inherit",
                  }}
                >
                  {tab.label}
                </Typography>
                <Box
                  width={22}
                  height={22}
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    backgroundColor: isSelected
                      ? alpha(theme.palette.common.white, 0.28)
                      : countTone.bg,
                  }}
                >
                  <Typography
                    variant="caption"
                    mt={0.2}
                    sx={{
                      color: isSelected
                        ? theme.palette.common.white
                        : countTone.color,
                      lineHeight: 1.2,
                      fontWeight: 700,
                    }}
                  >
                    {tab.count}
                  </Typography>
                </Box>
              </Box>
            }
          />
        );
      })}
    </Tabs>
  );
};

FGTrackingTabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      count: PropTypes.number.isRequired,
    }),
  ).isRequired,
  selectedTab: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default memo(FGTrackingTabs);
